import roundRect from './roundRect';
import { PREP_TOOL_LABEL } from './annotationConstants';
import { calculateRotatedLabelPosition, drawCounterRotatedLabel } from './labelPositionUtils';

/**
 * Calculates the scale factor for label dimensions based on zoom level.
 * Returns a scale factor that keeps labels visible but prevents them from becoming too large.
 * @param zoomLevel - The current zoom level (e.g., 1.0 = 100%, 2.0 = 200%)
 * @returns A scale factor between 0.5 and 1.0
 */
function calculateLabelScale(zoomLevel: number): number {
  // At 100% zoom (1.0), use full size (scale = 1.0)
  // At 200% zoom (2.0), use half size (scale = 0.5)
  // At 400% zoom (4.0), use quarter size (scale = 0.25)
  // But clamp to minimum of 0.5 to keep labels readable
  const baseScale = 1.0 / zoomLevel;
  return Math.max(0.5, Math.min(1.0, baseScale));
}

/**
 * Draws the markup label for annotations
 * @param pageRotation - The page rotation (0, 1, 2, 3 for 0°, 90°, 180°, 270°) to position label at perceived top-left
 */
function drawMarkupLabel(
  ctx: CanvasRenderingContext2D,
  labelText: string,
  x: number,
  y: number,
  height: number,
  labelMargin: number,
  paddingX: number,
  fillStyle: string,
  zoomLevel: number = 1.0, // Default to 100% zoom if not provided
  isGroupedDifferenceLabel: boolean = false,
  annotationWidth: number = 0, // Width of the annotation (needed for rotation calculations)
  annotationHeight: number = 0, // Height of the annotation (needed for rotation calculations)
  pageRotation: number = 0, // Page rotation (0, 1, 2, 3)
  pageWidth?: number,
  pageHeight?: number,
  containerBorderWidth: number = 0,
) {
  // Calculate scale factor based on zoom level
  const scale = calculateLabelScale(zoomLevel);

  // Scale all dimensions
  const scaledHeight = height * scale;
  const scaledLabelMargin = labelMargin * scale;
  const scaledPaddingX = paddingX * scale;

  const scaledFontSize = Math.max(8, Math.round(PREP_TOOL_LABEL.FONT_SIZE * scale)); // Minimum 8px for readability
  const scaledFont = `${PREP_TOOL_LABEL.FONT_WEIGHT} ${scaledFontSize}px ${PREP_TOOL_LABEL.FONT_FAMILY}`;

  ctx.font = scaledFont;
  // get Size of text to calculate size of label rectangle
  const { width: textWidth } = ctx.measureText(labelText);

  const labelWidth = textWidth + scaledPaddingX;
  const labelHeight = scaledHeight;

  // When grouped difference label is cut off at top, add space for popup buttons
  const POPUP_BUTTON_HEIGHT = 5 * scale; // Scale button height too
  const scaledBorderRadius = 2 * scale;
  const scaledTextPadding = 7 * scale;

  // Use rotation-aware positioning and drawing if annotation dimensions are provided and page is rotated
  if (annotationWidth > 0 && annotationHeight > 0 && pageRotation !== 0) {
    const bounds = { x, y, width: annotationWidth, height: annotationHeight };
    const labelDims = { width: labelWidth, height: labelHeight };
    const labelPosition = calculateRotatedLabelPosition(
      bounds,
      labelDims,
      pageRotation,
      scaledLabelMargin,
      false,
      pageWidth,
      pageHeight,
    );

    // Draw the complete label with counter-rotation so it always appears horizontal
    drawCounterRotatedLabel(
      ctx,
      labelText,
      labelPosition.x,
      labelPosition.y,
      labelWidth,
      labelHeight,
      pageRotation,
      fillStyle,
      scaledTextPadding,
      scaledBorderRadius,
      undefined, // no icon
      12, // iconSize
      2, // iconPadding
      scaledFont, // pass the font explicitly
      containerBorderWidth,
    );
  } else {
    // Original logic for no rotation or when annotation dimensions not provided
    const isCutOff = y < scaledHeight + scaledLabelMargin;
    const additionalOffset = isGroupedDifferenceLabel && isCutOff ? POPUP_BUTTON_HEIGHT : 0;
    const borderOffset = containerBorderWidth / 2;
    let labelX = x - borderOffset;
    let labelY: number;
    if (isCutOff) {
      labelY = y + additionalOffset;
    } else {
      labelY = y - scaledHeight - scaledLabelMargin;
    }
    if (pageWidth !== undefined && labelX + labelWidth > pageWidth) {
      labelX = Math.max(0, pageWidth - labelWidth);
    }

    ctx.fillStyle = fillStyle;
    roundRect(ctx, labelX, labelY, labelWidth, labelHeight, scaledBorderRadius);
    ctx.fill();

    // draw text inside label rectangle
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#FFFFFF';
    const scaledTextOffsetX = 7 * scale;
    const scaledTextOffsetY = 5 * scale;
    ctx.fillText(labelText, labelX + scaledTextOffsetX, labelY + labelHeight - scaledTextOffsetY);
  }
}

export default drawMarkupLabel;
